import { z } from "zod";

const videoAssetSchema = z.object({
  id: z.string(),
  type: z.string(),
  url: z.string(),
  createdAt: z.number(),
  sizeBytes: z.number().nullish(),
});

const renderStatsSchema = z.object({
  renderSeconds: z.number().nullish(),
  assetDownloadSeconds: z.number().nullish(),
  convertProtoSeconds: z.number().nullish(),
  peakMemoryBytes: z.number().nullish(),
  avgMemoryBytes: z.number().nullish(),
});

// =============================================================================
// Rendering Results schemas
// =============================================================================

const renderingResultSummarySchema = z.object({
  id: z.string(),
  projectProtoUri: z.string().nullish(),
  exportSettingsJson: z.string().nullish(),
  status: z.enum(["SUCCESS", "RENDER_FAILED"]),
  error: z.string().nullish(),
  createdAt: z.number(),
  videoAssetId: z.string().nullish(),
  renderStats: renderStatsSchema.nullish(),
  jerboaStdout: z.string().nullish(),
  gpuInfo: z
    .object({
      adapter: z.string(),
      backend: z.string(),
      deviceType: z.string(),
      driver: z.string(),
      isSoftware: z.boolean(),
      msaaSamples: z.number(),
      diagnostic: z.string().nullish(),
      version: z.string().nullish(),
    })
    .nullish(),
  projectType: z.string().nullish(),
  captionsProjectId: z.string().nullish(),
});

const renderingResultDetailSchema = renderingResultSummarySchema.extend({
  videoAsset: videoAssetSchema.nullish(),
});

export const renderingResultsListResponseSchema = z.object({
  results: z.object({
    page: z.array(renderingResultSummarySchema),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      endCursor: z.string().nullable(),
    }),
  }),
});

export const renderingResultDetailResponseSchema = z.object({
  result: renderingResultDetailSchema,
});

// =============================================================================
// Rendering analysis (server-export aggregated metrics) schemas
// =============================================================================

const renderingAnalysisBucketSchema = z.object({
  bucketStart: z.number(),
  bucketEnd: z.number(),
  totalCount: z.number(),
  successCount: z.number(),
  renderFailedCount: z.number(),
  successRate: z.number(),
  renderSecondsMedian: z.number(),
  renderSecondsP25: z.number(),
  renderSecondsP75: z.number(),
  renderSecondsP95: z.number(),
  // Length-normalized render speed (seconds per output frame).
  renderSecPerFrameMedian: z.number().nullish(),
  renderSecPerFrameP95: z.number().nullish(),
  // peak = OOM spike; avg = representative steady-state footprint (chart p50).
  peakMemoryBytesMedian: z.number(),
  avgMemoryBytesMedian: z.number().nullish(),
  captionsOnlyCount: z.number().nullish(),
  captionsOnlySuccessRate: z.number().nullish(),
  aiEditCount: z.number().nullish(),
  aiEditSuccessRate: z.number().nullish(),
  jerboaVersions: z.array(z.string()).nullish(),
  jerboaPrimaryVersion: z.string().nullish(),
  jerboaPrimaryVersionShare: z.number().nullish(),
  jerboaVersionCoverage: z.number().nullish(),
  releaseMarkers: z.array(z.object({ version: z.string(), firstSeenAt: z.number() })).nullish(),
});

export type RenderingAnalysisBucket = z.infer<typeof renderingAnalysisBucketSchema>;

export const renderingAnalysisResponseSchema = z.object({
  analysis: z.object({
    buckets: z.array(renderingAnalysisBucketSchema),
  }),
});

// =============================================================================
// Client report browser info schemas
// =============================================================================

export const jerboaBrowserInfoSchema = z.object({
  browserName: z.string().nullish(),
  userAgent: z.string().nullish(),
  deviceMemoryGb: z.number().nullish(),
  hardwareConcurrency: z.number().nullish(),
  appVersion: z.string().nullish(),
  os: z.string().nullish(),
  osVersion: z.string().nullish(),
  screenWidth: z.number().nullish(),
  screenHeight: z.number().nullish(),
  viewportWidth: z.number().nullish(),
  viewportHeight: z.number().nullish(),
  devicePixelRatio: z.number().nullish(),
  gpuRenderer: z.string().nullish(),
  gpuVendor: z.string().nullish(),
  language: z.string().nullish(),
  timezone: z.string().nullish(),
});

export type JerboaBrowserInfo = z.infer<typeof jerboaBrowserInfoSchema>;
